import { CustomOption, productAtom } from "core/catalog";
import { useMemo } from "react";
import { selectedCustomOptionsAtom } from "../atoms";

/**
 * Hook to validate if all required custom options have selected values
 * @returns boolean - true if all required options are filled, false otherwise
 */
export function useValidateRequiredCustomOptions() {
  const product = productAtom.use("product");
  const customOptions: CustomOption[] = useMemo(
    () => product?.customOptions || [],
    [product],
  );
  const selectedOptions = selectedCustomOptionsAtom.useValue();

  const isValid = useMemo(() => {
    // If there are no custom options, it's always valid
    if (customOptions.length === 0) return true;

    // Get all required custom options
    const requiredOptions = customOptions.filter(
      (option) => option.required === true,
    );

    // If there are no required options, it's valid
    if (requiredOptions.length === 0) return true;

    // Create a map of selected options for quick lookup
    const selectedOptionsMap = new Map<string, boolean>();
    selectedOptions.forEach((option) => {
      // Check if the option has a valid value
      const hasValue =
        option.value !== undefined &&
        option.value !== null &&
        option.value !== "" &&
        (Array.isArray(option.value) ? option.value.length > 0 : true);

      selectedOptionsMap.set(option.optionId, hasValue);
    });

    // Check if all required options have valid values
    const allRequiredFilled = requiredOptions.every((option) => {
      const hasSelectedValue = selectedOptionsMap.get(option.id);

      // If not found in selected options, check if it has a default value
      if (!hasSelectedValue && option.defaultValue !== undefined) {
        const hasDefaultValue =
          option.defaultValue !== null &&
          option.defaultValue !== "" &&
          (Array.isArray(option.defaultValue)
            ? option.defaultValue.length > 0
            : true);

        return hasDefaultValue;
      }

      return hasSelectedValue === true;
    });

    return allRequiredFilled;
  }, [customOptions, selectedOptions]);

  return isValid;
}
